// Cross-tab session coordination (2026-10-06).
//
// Membership changes do not sign the user out of Firebase Auth, so Auth's own
// cross-tab propagation is not enough. A tab that learns authoritative access
// changed tells the others, which immediately leave ACTIVE and clear their
// firm-sensitive state. BroadcastChannel where supported; a localStorage
// 'storage' event as the fallback. Messages carry only an event type, a
// timestamp and a random per-tab id — never uids, firm ids or data.
export const SESSION_EVENTS = Object.freeze({
  LOGOUT: 'LOGOUT',
  TENANT_CHANGED: 'TENANT_CHANGED',
  FIRM_ACCESS_LOST: 'FIRM_ACCESS_LOST',
  PERSISTENCE_RESET: 'PERSISTENCE_RESET',
});

const CHANNEL = 'firmassist-session';
const STORAGE_KEY = 'firmassist.sessionEvent';
// Random per-window id, so a tab ignores its own messages.
const tabIdOf = (env) => {
  try {
    if (!env.__firmassistSessionTab) Object.defineProperty(env, '__firmassistSessionTab', { value: Math.random().toString(36).slice(2) });
    return env.__firmassistSessionTab;
  } catch (_) { return 'tab'; }
};

const valid = (m, env) => !!m && typeof m === 'object' && Object.prototype.hasOwnProperty.call(SESSION_EVENTS, m.type) && m.tab !== tabIdOf(env);

export function postSessionEvent(type, env = (typeof window !== 'undefined' ? window : undefined)) {
  if (!env || !SESSION_EVENTS[type]) return;
  const msg = { type, at: Date.now(), tab: tabIdOf(env) };
  let sent = false;
  try {
    if (typeof env.BroadcastChannel === 'function') {
      const ch = new env.BroadcastChannel(CHANNEL);
      ch.postMessage(msg);
      ch.close();
      sent = true;
    }
  } catch (_) { /* fall through to storage */ }
  if (!sent) {
    try {
      env.localStorage.setItem(STORAGE_KEY, JSON.stringify(msg));
      env.localStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* storage unavailable — Firestore listeners remain the backstop */ }
  }
}

export function subscribeSessionEvents(handler, env = (typeof window !== 'undefined' ? window : undefined)) {
  if (!env) return () => {};
  const unsubs = [];
  try {
    if (typeof env.BroadcastChannel === 'function') {
      const ch = new env.BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => { if (valid(e.data, env)) handler(e.data); };
      unsubs.push(() => ch.close());
    }
  } catch (_) { /* ignore */ }
  // The storage fallback is always listened to (a sender without
  // BroadcastChannel can only use it); duplicates are harmless — handlers are idempotent.
  const onStorage = (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try { const m = JSON.parse(e.newValue); if (valid(m, env)) handler(m); } catch (_) { /* ignore */ }
  };
  try { env.addEventListener('storage', onStorage); unsubs.push(() => env.removeEventListener('storage', onStorage)); } catch (_) { /* ignore */ }
  return () => unsubs.forEach((u) => u());
}
